import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '@services/authService';

const MICROFRONTEND_URL = (import.meta as any).env?.VITE_BUILD_SOFTWARE_MFE_URL || 'http://localhost:5176';
// postMessage targetOrigin must be scheme+host+port only (no path)
const MICROFRONTEND_ORIGIN = new URL(MICROFRONTEND_URL).origin;

/**
 * BuildSoftwareWrapper - Iframe wrapper for the Build Custom Software microfrontend
 *
 * Handles:
 * - Embedding the microfrontend via iframe
 * - postMessage communication for authentication
 * - Navigation events from the microfrontend
 */
export function BuildSoftwareWrapper() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Safety guard: if this component is running inside an iframe (e.g. because the
  // microfrontend S3 bucket is empty and CloudFront fell back to main app's index.html),
  // do not create another iframe — that would cause infinite recursive embedding.
  if (window.self !== window.top) {
    return null;
  }

  console.log('BuildSoftwareWrapper: projectId from query params:', projectId);

  // Strip trailing slash if present to avoid //wizard paths
  const baseUrl = MICROFRONTEND_URL.replace(/\/$/, '');

  // Build iframe URL - always start at wizard
  const iframeSrc = projectId
    ? `${baseUrl}/wizard?projectId=${projectId}`
    : `${baseUrl}/wizard`;

  console.log('BuildSoftwareWrapper: iframe src:', iframeSrc);

  // Send auth token to iframe — always ensure freshness first
  const sendAuthToken = useCallback(async () => {
    try {
      await authService.getKeycloakInstance()?.updateToken(60);
    } catch {
      // Ignore
    }
    const token = authService.getToken();
    console.log('BuildSoftwareWrapper: Sending auth token to microfrontend:', token ? 'token present' : 'no token');
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'AUTH_TOKEN', token: token || null },
      MICROFRONTEND_ORIGIN
    );
  }, []);

  // Handle messages from iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Only accept messages from microfrontend origin
      if (event.origin !== MICROFRONTEND_ORIGIN) return;

      const { type, ...data } = event.data || {};

      switch (type) {
        case 'REQUEST_AUTH': {
          // Get auth token from Keycloak service and send it
          sendAuthToken();
          break;
        }

        case 'NAVIGATE': {
          // Navigate to a new path in the main app
          const path = data.path as string;
          if (path) {
            navigate(path);
          }
          break;
        }

        case 'PLAN_CREATED': {
          // Handle plan creation notification
          const planId = data.planId as string;
          console.log('Plan created:', planId);
          break;
        }

        case 'PLAN_COMPLETED': {
          // Handle plan completion notification
          const planId = data.planId as string;
          console.log('Plan completed:', planId);
          // Could navigate back to dashboard or show success message
          break;
        }

        default:
          break;
      }
    },
    [navigate, sendAuthToken]
  );

  // Set up message listener
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Push a fresh token every 4 minutes to keep long-running operations authenticated
  useEffect(() => {
    const interval = setInterval(() => {
      if (iframeRef.current) {
        sendAuthToken();
      }
    }, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [sendAuthToken]);

  // Handle iframe load - proactively send auth token
  const handleIframeLoad = useCallback(() => {
    console.log('BuildSoftwareWrapper: Iframe loaded, sending auth token proactively');
    // Small delay to ensure the iframe's message listener is set up
    setTimeout(() => {
      sendAuthToken();
    }, 100);
  }, [sendAuthToken]);

  return (
    <div className="h-[calc(100vh-64px)] w-full -m-8">
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Build Custom Software"
        className="w-full h-full border-0"
        allow="clipboard-write"
        onLoad={handleIframeLoad}
      />
    </div>
  );
}
